import { css } from '@emotion/react';
import {
  type FC, Fragment, useCallback, useEffect, useRef, useState,
} from 'react';
import { type Pagination } from '~/_shared/types/pagination/pagination';
import { type ShareMapProps } from '~/map/share/shareMap.container';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { type MapListingItem } from '../item/mapListingItem';
import { type MapListingRowItem } from '../tableData/mapRow/mapRow.component';
import { MobileMapRowComponent } from './mapRow/mobileMapRow.component';

const tableStyle = css({
  width: '100%',
  borderSpacing: 0,
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
});

type MobileTableDataProps = Readonly<{
  data: ReadonlyArray<MapListingItem>;
  isMapListingDataLoading: boolean;
  pagination: Pagination | null;
  toggledMapIds: ReadonlyArray<number>;

  onMapShareSettingsClick: (props: ShareMapProps) => void;
  onPageChange: (page: number) => void;
  onShowSubMapsToggle: (mapId: number) => void;
}>;

export const MobileTableDataComponent: FC<MobileTableDataProps> = (props) => {
  const { isMapListingDataLoading, pagination, onPageChange } = props;
  const currentUserId = useUserIdSelector();
  const observer = useRef<IntersectionObserver | null>(null);
  const [isLock, setIsLock] = useState<boolean>(false);
  const lastRef = useCallback(
    (node: HTMLTableRowElement) => {
      if (isMapListingDataLoading || isLock) {
        return;
      }
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting && pagination && onPageChange && pagination.lastPage > pagination.currentPage) {
          setIsLock(true);
          onPageChange(pagination.currentPage + 1);
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    [isMapListingDataLoading, pagination, onPageChange, isLock],
  );

  useEffect(() => {
    if (!isMapListingDataLoading) {
      setIsLock(false);
    }
  }, [isMapListingDataLoading]);

  return (
    <table
      css={tableStyle}
      data-testid="mapList"
    >
      <tbody>
        {props.data.map((item, index) => {
          const areSubMapsExpanded = props.toggledMapIds.includes(item.id);
          const isLastElement = index + 1 === props.data.length;

          return (
            <Fragment key={item.id} >
              <MobileMapRowComponent
                setRef={isLastElement ? lastRef : undefined}
                areSubMapsExpanded={areSubMapsExpanded}
                index={index}
                item={item}
                onShowSubMapsToggle={() => props.onShowSubMapsToggle(item.id)}
                onMapShareSettingsClick={props.onMapShareSettingsClick}
              />

              {areSubMapsExpanded && item.layering?.connected && item.layering?.baseMaps.map((baseMap, subIndex) => {
                const hasAccess = baseMap.sharedUsers.some(user => user.id === currentUserId);
                const baseMapItem: MapListingRowItem = {
                  ...baseMap,
                  isBaseLayer: true,
                  noAccess: !hasAccess,
                  snapshots: [],
                };

                return (
                  <MobileMapRowComponent
                    areSubMapsExpanded={false}
                    index={subIndex}
                    item={baseMapItem}
                    key={baseMapItem.id + 'base_map'}
                    parentMapItem={item}
                    onMapShareSettingsClick={props.onMapShareSettingsClick}
                  />
                );
              })}

              {areSubMapsExpanded && item.snapshots.map((snapshot, subIndex) => (
                <MobileMapRowComponent
                  areSubMapsExpanded={false}
                  index={subIndex}
                  item={snapshot}
                  key={snapshot.id + 'snapshot'}
                  parentMapItem={item}
                  onMapShareSettingsClick={props.onMapShareSettingsClick}
                />
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};
